import axios from '@/utils/axios.js'
import { getFingerprint } from '@/utils/fingerprint.js'

export async function getMangaInfo(dto) {
  const { data } = await axios.post('/manga/getMangaInfo', dto)
  return data
}

/**
 * 用户-获取翻译历史列表（分页）
 * @param {Object} params 查询参数 { pageNo, pageSize, fileName, status, channelType }
 * @returns {Promise} 分页数据
 */
export async function getUserTranslationHisList(params) {
  const { data } = await axios.get('/mangaCenter/translationHis/user/list', { params })
  return data
}

/**
 * 用户-根据id获取翻译记录详情（含 translationJson，在线编辑用）
 * @param {Number} id 记录id
 * @returns {Promise} 记录详情
 */
export async function getUserTranslationHisById(id) {
  const { data } = await axios.get('/mangaCenter/translationHis/user/queryById', { params: { id } })
  return data
}

export async function getTeam() {
  const { data } = await axios.get('/manga/getTeam')
  return data
}

export async function getVipTeam() {
  const { data } = await axios.get('/mangaCenter/translationHis/getTeam')
  return data
}

/**
 * 准备翻译 - 上传文件到翻译队列
 * @param {Array} files 文件数组
 * @param {Object} config 翻译配置（可选）
 * @param {Function} onProgress 进度回调（兼容旧接口签名，可选）
 * @returns {Promise} 上传结果
 */
export async function prepareTranslation(files, config, onProgress) {
  const formData = new FormData()
  files.forEach((file) => {
    const actualFile = file.raw || file
    formData.append('file', actualFile)
  })
  if (config) {
    formData.append('config', new Blob([JSON.stringify(config)], { type: 'application/json' }))
  }

  const { data } = await axios({
    method: 'post',
    url: '/mangaCenter/translationHis/prepare/translation',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
    onUploadProgress: onProgress ? (e) => {
      if (e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    } : undefined,
  })
  return data
}

/**
 * 用户-删除自己的翻译记录
 * @param {Number} id 记录id
 * @returns {Promise}
 */
export async function deleteUserTranslationHis(id) {
  const { data } = await axios.delete('/mangaCenter/translationHis/user/delete', { params: { id } })
  return data
}

/**
 * 用户-清空已完成/失败的翻译记录
 * @returns {Promise}
 */
export async function clearUserTranslationHis() {
  const { data } = await axios.delete('/mangaCenter/translationHis/user/clear')
  return data
}

/**
 * 用户-获取算力订单列表（分页）
 * @param {Object} params 查询参数 { pageNo, pageSize }
 * @returns {Promise} 分页数据
 */
export async function getUserPowerOrderList(params) {
  const { data } = await axios.get('/mangaCenter/powerOrder/user/list', { params })
  return data
}

/**
 * 创建支付宝PC网页支付订单（测试）
 * @returns {Promise} 支付宝表单HTML
 */
export async function createPagePayOrder() {
  const { data } = await axios.post('/manga/pay/createPagePayOrder', null, {
    params: { totalAmount: '0.1', subject: '测试' }
  })
  return data
}

/**
 * 创建订单并发起支付
 * @param {Object} params { commodityId, payType }
 * @returns {Promise} 支付宝表单HTML 或 微信二维码链接
 */
export async function createPayOrder(params) {
  const { data } = await axios.post('/manga/pay/createPayOrder', params)
  return data
}

/**
 * 查询微信支付订单状态
 * @param {String} outTradeNo 商户订单号
 * @returns {Promise} 交易状态
 */
export async function queryWxTradeStatus(outTradeNo) {
  const { data } = await axios.get('/manga/pay/queryWxTradeStatus', { params: { outTradeNo } })
  return data
}

/**
 * 捕获PayPal订单（确认扣款）
 * @param {String} paypalOrderId PayPal订单ID
 * @param {String} outTradeNo 商户订单号
 * @returns {Promise} 捕获结果
 */
export async function capturePayPalOrder(paypalOrderId, outTradeNo) {
  const { data } = await axios.post('/manga/pay/capturePayPalOrder', null, {
    params: { paypalOrderId, outTradeNo }
  })
  return data
}

/**
 * 查询PayPal订单状态
 * @param {String} paypalOrderId PayPal订单ID
 * @returns {Promise} 订单状态
 */
export async function queryPayPalOrderStatus(paypalOrderId) {
  const { data } = await axios.get('/manga/pay/queryPayPalOrderStatus', { params: { paypalOrderId } })
  return data
}

/**
 * 获取算力商品列表
 * @returns {Promise} 商品列表
 */
export async function getCommodityList() {
  const { data } = await axios.get('/mangaCenter/commodity/list', { params: { type: 1 } })
  return data
}

/**
 * 批量上传漫画图片（免费队列）
 * @param {Array} files 文件数组
 * @param {Object} config 翻译配置（可选）
 * @param {Function} onProgress 进度回调（可选）
 * @returns {Promise} 上传结果
 */
export async function batchMangaUpload(files, config, onProgress) {
  const formData = new FormData()
  files.forEach((file) => {
    const actualFile = file.raw || file
    formData.append('files', actualFile)
  })

  if (config) {
    formData.append('config', new Blob([JSON.stringify(config)], { type: 'application/json' }))
  }

  // 添加浏览器指纹用于防刷
  const fingerprint = await getFingerprint()
  formData.append('fingerprint', fingerprint)

  const { data } = await axios({
    method: 'post',
    url: '/manga/user/upload/batch',
    headers: { 'Content-Type': 'multipart/form-data' },
    data: formData,
    onUploadProgress: onProgress ? (e) => {
      if (e.total) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    } : undefined,
  })
  return data
}
